import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { SortOrder } from 'types/interfaces/sort';

interface SortState {
  sort: string | undefined;
  direction: SortOrder | undefined;
}

function parseSortCompound(value: string): SortState | null {
  const lastDash = value.lastIndexOf('-');
  if (lastDash <= 0) return null;
  const sort = value.substring(0, lastDash);
  const direction = value.substring(lastDash + 1);
  if (direction !== 'asc' && direction !== 'desc') return null;
  return { sort, direction };
}

/**
 * Persists sort state to URL query params and localStorage.
 * Reads from URL param `?sort=field-direction`, falls back to localStorage, then default.
 * Syncs changes to both URL (replace) and localStorage.
 */
export function useSortPreference(
  storageKey: string,
  defaultSort: string,
  defaultDirection: SortOrder
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultCompound = `${defaultSort}-${defaultDirection}`;

  const getInitialSort = (): SortState => {
    const urlSort = searchParams.get('sort');
    if (urlSort) {
      const parsed = parseSortCompound(urlSort);
      if (parsed) return parsed;
    }

    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const parsed = parseSortCompound(saved);
      if (parsed) return parsed;
    }

    return { sort: defaultSort, direction: defaultDirection };
  };

  const [orderBy, setOrderByState] = useState<SortState>(getInitialSort);

  const compound = `${orderBy.sort}-${orderBy.direction}`;

  // Sync to URL and localStorage when sort changes
  useEffect(() => {
    // Always persist to localStorage
    localStorage.setItem(storageKey, compound);

    // Update URL params
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (compound === defaultCompound) {
        newParams.delete('sort');
      } else {
        newParams.set('sort', compound);
      }
      return newParams;
    }, { replace: true });
  }, [compound, defaultCompound, storageKey, setSearchParams]);

  const setOrderBy: React.Dispatch<React.SetStateAction<SortState>> = useCallback(
    (value) => {
      setOrderByState(value);
    },
    []
  );

  return { orderBy, setOrderBy, compound };
}
